/**
 * Statement-presence checks for financial records.
 *
 * A company is only analysable when BOTH the Profit & Loss and the Balance Sheet
 * are present. We key off RAW line items only — never the derived fields
 * (net_debt, invested_capital, ebitda_margin), which can be computed even when
 * the underlying statement isn't there and would otherwise mask a missing one.
 *
 * We require at least one NON-ZERO value, not merely non-null: the India
 * MoneyControl ingestion zero-fills every line item of a statement it can't map
 * (financial_service.py defaults missing fields to 0.0), so an absent Balance
 * Sheet arrives as all-zeros, not nulls. The multi-field `some` test keeps a
 * legitimately debt-free company (total_debt = 0, current_investments = 0, …)
 * passing — a real statement always has an anchor field (total_assets /
 * shareholders_funds for BS, total_revenue / net_profit for P&L) that is
 * strictly non-zero, while an all-zero (missing) statement correctly fails.
 */
import type { UnifiedFinancialRecord } from '../../../types/financial';

const isRealValue = (v: unknown): boolean =>
  typeof v === 'number' && !Number.isNaN(v) && v !== 0;

const PL_KEYS: (keyof UnifiedFinancialRecord)[] = [
  'total_revenue',
  'revenue_from_operations',
  'gross_profit',
  'ebitda',
  'ebit',
  'pbt',
  'net_profit',
  'eps',
  'direct_cost',
  'employee_expenses',
  'other_expenses',
  'finance_costs',
  'depreciation_amortisation',
  'total_expenses',
];

const BS_KEYS: (keyof UnifiedFinancialRecord)[] = [
  'total_assets',
  'total_current_assets',
  'fixed_assets',
  'cash_and_equivalents',
  'current_investments',
  'total_liabilities',
  'total_current_liabilities',
  'total_non_current_liabilities',
  'trade_payables',
  'shareholders_funds',
  'total_debt',
];

export function hasProfitLoss(records: UnifiedFinancialRecord[]): boolean {
  return records.some((r) => PL_KEYS.some((k) => isRealValue(r[k])));
}

export function hasBalanceSheet(records: UnifiedFinancialRecord[]): boolean {
  return records.some((r) => BS_KEYS.some((k) => isRealValue(r[k])));
}
